import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/**
 * Мягкая плашка «Вышла новая версия» (15.10.2026, решение владельца вместо OTA): «Обновить» — ссылка в магазин,
 * «Позже» прячет её на N дней (`lib/updateLater.ts`). Когда показывать — решает `UpdateGate` по `latestVersion`.
 * Жёсткий экран «Нужно обновить приложение» по `minVersion` — отдельно и важнее (`UpdateRequiredScreen`).
 *
 * Оформление — как у всплывашек приложения (`NetworkToast`): снизу по центру, над нижним меню кабинета, от `md` —
 * у края. Карточка — готовый `Card`, «Позже» — `Button` ghost, «Обновить» — ссылка в виде `Button` primary; своих
 * цветов нет.
 *
 * ⚠️ `translate="no"` и текст только внутри `<span>` — от переводчика браузера (техдолг 263).
 * ⚠️ `data-native-video-cover` — как у `OfflineBar` и всплывашек: нативный ролик урока лежит поверх страницы и
 * иначе закрыл бы плашку (`lib/inlineVideoNative.ts`).
 */
/**
 * «Обновить» — ссылка `<a href>` тем же путём, что «Обновить» на жёстком экране (`UpdateRequiredScreen`): один путь
 * на оба экрана, без сюрпризов в WKWebView. Классы — те же, что `Button` primary размера sm (`components/ui/Button.tsx`),
 * своих цветов нет; сторож — `lib/updateAvailable.test.ts`.
 */
const STORE_LINK_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-button font-display font-semibold transition active:scale-[0.98] ' +
  'h-8 px-3 text-sm text-on-primary bg-gradient-to-b from-primary to-primary-pressed shadow-soft hover:shadow-ambient hover:brightness-105';

export function UpdateBanner({ storeUrl, onLater }: { storeUrl: string; onLater: () => void }) {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-live="polite"
      translate="no"
      data-native-video-cover=""
      data-update-banner=""
      className="pointer-events-none fixed inset-x-0 bottom-[calc(4.75rem_+_var(--sa-bottom))] z-50 flex justify-center px-4 md:bottom-6"
    >
      <Card className="pointer-events-auto w-full max-w-md p-4">
        <div className="flex items-start gap-3">
          <span className="material-symbols-outlined text-2xl text-primary" aria-hidden="true">
            download
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-bold text-on-surface">
              <span>{t('updateAvailable.title')}</span>
            </p>
            <p className="mt-0.5 text-sm text-on-surface-variant">
              <span>{t('updateAvailable.text')}</span>
            </p>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onLater}>
            {t('updateAvailable.later')}
          </Button>
          {/* Ссылка, как на жёстком экране (решение владельца 15.10.2026): Capacitor отдаёт её системе, App Store
              открывается сам — проверено в 1.1.0/1.1.1. Вид — ровно `Button` primary sm. */}
          <a href={storeUrl} className={STORE_LINK_CLASS}>
            <span>{t('updateAvailable.cta')}</span>
          </a>
        </div>
      </Card>
    </div>
  );
}
